import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, FileText, Shield, AlertTriangle, RefreshCw,
} from "lucide-react";

function StatusDot({ status }) {
  const colors = { "in-session": "#68d391", available: "#63b3ed", documenting: "#ecc94b", traveling: "#f6ad55", offline: "#4a5568" };
  return (
    <span style={{
      width: 8, height: 8, borderRadius: "50%", display: "inline-block",
      background: colors[status] || "#4a5568",
      boxShadow: status === "in-session" ? "0 0 8px #68d391" : "none",
    }} />
  );
}

export default function OverviewTab({
  C, teamMembers, teamLoading, inSession, unreviewedAlerts, allNotes,
  todayNotes, pendingReview, avgConfidence, safetyAlerts, setActiveTab,
}) {
  const navigate = useNavigate();

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Team Members", value: teamMembers.length, color: "#63b3ed" },
          { label: "In Session Now", value: inSession.length, color: "#68d391" },
          { label: "Safety Alerts", value: unreviewedAlerts, color: unreviewedAlerts > 0 ? "#fc8181" : "#68d391" },
          { label: "Notes Today", value: todayNotes.length, color: "#ecc94b" },
          { label: "Pending Review", value: pendingReview.length, color: "#f6ad55" },
          { label: "Avg Confidence", value: avgConfidence > 0 ? `${avgConfidence}%` : "—", color: "#4fd1c5" },
        ].map((kpi, i) => (
          <div key={i} style={C.card}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1.5 }}>{kpi.label}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: kpi.color, marginTop: 4, fontFamily: "monospace" }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Team Live Status */}
        <div style={C.card}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>Team Status</span>
            <button onClick={() => setActiveTab("team")} style={{ fontSize: 11, color: "#63b3ed", fontWeight: 700, background: "none", border: "none", cursor: "pointer" }}>View All →</button>
          </div>
          {teamMembers.length === 0 && !teamLoading ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Users size={24} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
              <div style={{ fontSize: 12, color: "#4a5568" }}>No team members found</div>
            </div>
          ) : teamLoading ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <RefreshCw size={18} style={{ color: "#63b3ed", margin: "0 auto 8px", animation: "spin 1s linear infinite" }} />
              <div style={{ fontSize: 12, color: "#4a5568" }}>Loading team...</div>
            </div>
          ) : (
            teamMembers.slice(0, 6).map((m, i) => (
              <div key={m.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 0",
                borderBottom: i < Math.min(teamMembers.length - 1, 5) ? "1px solid rgba(99,179,237,0.06)" : "none",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <StatusDot status={m.liveStatus || "offline"} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{m.displayName || m.email}</div>
                    <div style={{ fontSize: 10, color: "#718096" }}>
                      {m.role} · {m.credentials || "—"}
                      {m.currentIndividual && <span style={{ color: "#68d391" }}> · w/ {m.currentIndividual}</span>}
                    </div>
                  </div>
                </div>
                <span style={{ fontSize: 10, color: "#4a5568", textTransform: "capitalize" }}>{(m.liveStatus || "offline").replace("-", " ")}</span>
              </div>
            ))
          )}
        </div>

        {/* Safety Alerts */}
        <div style={C.card}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Shield size={14} style={{ color: "#4fd1c5" }} />
              <span style={{ fontSize: 14, fontWeight: 800 }}>Safety Alerts</span>
            </div>
            <button onClick={() => setActiveTab("safety")} style={{ fontSize: 11, color: "#63b3ed", fontWeight: 700, background: "none", border: "none", cursor: "pointer" }}>View All →</button>
          </div>
          {safetyAlerts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Shield size={24} style={{ color: "rgba(104,211,145,0.3)", margin: "0 auto 8px" }} />
              <div style={{ fontSize: 12, color: "#4a5568" }}>All clear — no safety alerts</div>
            </div>
          ) : (
            safetyAlerts.slice(0, 4).map(alert => (
              <div key={alert.id} style={{
                padding: 12, borderRadius: 10, marginBottom: 8, cursor: "pointer",
                background: alert.safetyReviewed ? "rgba(15,23,42,0.5)" : "rgba(252,129,129,0.04)",
                border: `1px solid ${alert.safetyReviewed ? "rgba(99,179,237,0.06)" : "rgba(252,129,129,0.12)"}`,
              }} onClick={() => alert.noteData && navigate("/session/review", { state: { note: alert.noteData, readOnly: true } })}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "rgba(252,129,129,0.15)", color: "#fc8181", textTransform: "uppercase" }}>Priority</span>
                  {!alert.safetyReviewed && <span style={{ fontSize: 9, color: "#fc8181", fontWeight: 700 }}>● NEW</span>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>{alert.individualName || "Unknown"}</div>
                <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>{alert.agentName || ""} · {alert.date}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Notes */}
      <div style={C.card}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>Recent Notes</span>
          <button onClick={() => setActiveTab("notes")} style={{ fontSize: 11, color: "#63b3ed", fontWeight: 700, background: "none", border: "none", cursor: "pointer" }}>View All →</button>
        </div>
        {allNotes.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <FileText size={28} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
            <div style={{ fontSize: 13, color: "#4a5568" }}>No notes yet</div>
          </div>
        ) : (
          allNotes.slice(0, 8).map((note, i) => (
            <div key={note.id || i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0",
              borderBottom: i < Math.min(allNotes.length - 1, 7) ? "1px solid rgba(99,179,237,0.06)" : "none",
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{note.individualName || "Unknown"}</div>
                <div style={{ fontSize: 10, color: "#718096" }}>
                  {note.agentName ? `${note.agentName} · ` : ""}{note.date} · {note.serviceType} · {note.duration}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {note.hasPriorityFlags && <AlertTriangle size={11} style={{ color: "#fc8181" }} />}
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                  background: note.status === "signed" ? "rgba(104,211,145,0.1)" : note.status === "submitted" ? "rgba(236,201,75,0.1)" : "rgba(99,179,237,0.08)",
                  color: note.status === "signed" ? "#68d391" : note.status === "submitted" ? "#ecc94b" : "#718096",
                }}>
                  {note.status === "signed" ? "Signed" : note.status === "submitted" ? "Pending" : "Draft"}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}